import { Component, OnInit, ViewChild, EventEmitter } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { HttpEventType } from '@angular/common/http';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import * as XLSX from "xlsx";
import { getErrorMessage } from 'app/_utils/helpers';

import {
  AuthenticationService,
  CompanyService,
  AttendeeService,
} from 'app/_services';
import { COMPANY_STEPS, COMPANY_STEP_LINKS } from 'app/app.constants';

// The columns in assets/file/Attendee Import Template.xlsx (bureau template).
const TEMPLATE_COLUMNS = [
  'Last Name', 'First Name', 'Middle Name', 'Address 1', 'Address 2', 'City', 'State',
  'Zip', 'Country', 'Specialty', 'Other Specialty', 'NPI', 'License State', 'License #',
  'Telephone', 'Email', 'Suffix', 'Credentials', 'Professional Type', 'Other Credentials',
  'Client ID', 'Affiliation',
];

@Component({
  selector: 'app-attedee-import-modal',
  templateUrl: './attendee-import-modal.component.html',
  styleUrls: ['./attendee-import-modal.component.scss']
})
export class AttendeeImportModalComponent implements OnInit {
  public event: EventEmitter<any> = new EventEmitter();
  COMPANY_STEP_LINK = COMPANY_STEP_LINKS.filter((s) => s.step === COMPANY_STEPS.SPEAKER)[0];
  companyId: string;
  eventId: string;
  uploadForm: FormGroup;
  @ViewChild('xlsxFileInput') xlsxFileInput;
  errText: string = '';
  xlsxData: any[] = [];
  importedAttendees: any[] = [];
  importedResult: any[] = [];
  npiErrorItems: any[] = [];
  licenseErrorItems: any[] = [];
  bImport: Boolean = false;
  step: number = 0;

  // AI reformat state (POC).
  templateColumns = TEMPLATE_COLUMNS;
  rawHeaders: string[] = [];
  rawRows: any[] = [];
  aiState: 'idle' | 'matched' | 'loading' | 'reformatted' | 'error' = 'idle';
  aiMessage: string = '';
  aiWarnings: string[] = [];
  showOriginal: boolean = false;

  hostConfrimed: boolean = false;
  constructor(
    public bsModalRef: BsModalRef,
    private fb: FormBuilder,
    private toastr: ToastrService,
    private attendeeService: AttendeeService,
  ) {
    this.uploadForm = this.fb.group({
      xlsx_file: ['', Validators.required],
    });
  }
  ngOnInit() {

  }
  importData() {
    this.attendeeService.validateAttendees({
      company_id: this.companyId,
      event_id: this.eventId,
    }, this.xlsxData).subscribe((res) => {
      this.uploadForm.reset();
      this.importedAttendees = res;
      this.step = 1;
    }, (err) => {
      this.toastr.error(getErrorMessage(err));
    });
  }
  onComplete() {
    this.attendeeService.importAttendees({
      company_id: this.companyId,
      event_id: this.eventId,
    }, {
      attendees: this.importedAttendees,
      hostConfirmed: this.hostConfrimed
    }).subscribe((res) => {
      this.importedResult = res;
      this.step = 2;
      this.event.emit();
    }, (err) => {
      this.toastr.error(getErrorMessage(err));
    });
  }
  onFileChange(evt: any) {
    this.resetImportState();
    const target: DataTransfer = <DataTransfer>(evt.target);
    if (target.files.length > 1) {
      alert('Multiple files are not allowed');
      return;
    }
    const reader: FileReader = new FileReader();
    reader.onload = (e: any) => {
      const bstr: string = e.target.result;
      const wb: XLSX.WorkBook = XLSX.read(bstr, { type: 'binary' });
      const wsname = wb.SheetNames[0];
      const ws: XLSX.WorkSheet = wb.Sheets[wsname];
      const obj: any[] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
      const [headers, ...data] = obj as any[][];
      this.rawHeaders = (headers || []).map((h) => String(h || '').trim()).filter(Boolean);
      this.rawRows = data
        .map((row) => Object.fromEntries(this.rawHeaders.map((h, i) => [h, row[i]])))
        .filter((r: any) => Object.values(r).some((v) => v !== null && v !== undefined && String(v).trim() !== ''));

      if (this.headersMatchTemplate()) {
        // File already uses the template — behave exactly as before.
        this.aiState = 'matched';
        this.xlsxData = this.rawRows;
        this.runDuplicateCheck();
      } else if (this.rawRows.length) {
        // Columns don't match — clean & reformat with AI.
        this.reformatWithAi();
      } else {
        alert('no data to display!');
      }
    }
    reader.readAsBinaryString(target.files[0]);
  }

  private resetImportState() {
    this.xlsxData = [];
    this.npiErrorItems = [];
    this.licenseErrorItems = [];
    this.errText = '';
    this.bImport = false;
    this.rawHeaders = [];
    this.rawRows = [];
    this.aiState = 'idle';
    this.aiMessage = '';
    this.aiWarnings = [];
    this.showOriginal = false;
  }

  private headersMatchTemplate(): boolean {
    const headerSet = new Set(this.rawHeaders);
    const matched = TEMPLATE_COLUMNS.filter((c) => headerSet.has(c)).length;
    return headerSet.has('First Name') && headerSet.has('Last Name') && matched >= 3;
  }

  // POC: send raw rows to the backend AI, which splits/parses/standardizes each
  // value into the bureau template columns (address, license state+number,
  // affiliation, phone, etc.), then run the usual duplicate check.
  reformatWithAi() {
    if (!this.rawRows.length) return;
    this.aiState = 'loading';
    this.aiMessage = '';
    this.aiWarnings = [];
    this.bImport = false;
    this.attendeeService.reformatImportRows(TEMPLATE_COLUMNS, this.rawRows).subscribe((res) => {
      this.xlsxData = res?.rows || [];
      this.aiWarnings = res?.warnings || [];
      this.aiState = 'reformatted';
      this.aiMessage = `Reformatted ${this.xlsxData.length} attendee(s) from your file. Please review before importing.`;
      this.runDuplicateCheck();
    }, (err) => {
      this.aiState = 'error';
      this.toastr.error(getErrorMessage(err) || 'AI reformatting failed. Please use the template.');
    });
  }

  // Flag rows whose NPI or License repeats; enable Import only when clean.
  private runDuplicateCheck() {
    this.npiErrorItems = [];
    this.licenseErrorItems = [];
    if (!this.xlsxData.length) {
      this.bImport = false;
      return;
    }
    this.xlsxData.forEach((item) => {
      if (this.checkDuplicates(item, 'NPI')) {
        this.npiErrorItems.push(item);
      }
      if (this.checkDuplicates(item, 'License State') && this.checkDuplicates(item, 'License #')) {
        this.licenseErrorItems.push(item);
      }
    });
    if (!this.npiErrorItems.length && !this.licenseErrorItems.length) {
      this.errText = '';
      this.bImport = true;
    } else {
      this.errText = `The following data appears more than once:
                             NPI: ${this.npiErrorItems.length},
                             License : ${this.licenseErrorItems.length} `;
      this.bImport = false;
    }
  }
  onClick(evt: any) {
    evt.target.value = null;
  }
  checkDuplicates(item: any, field: string): boolean {
    const value = item[field];
    // Blank values (no NPI / no license) are not real duplicates.
    if (value === null || value === undefined || String(value).trim() === '') {
      return false;
    }
    const duplicates = this.xlsxData.filter(x => x[field] === value);
    return duplicates.length > 1;
  }
}
